import { createSystem, defaultConfig } from "@chakra-ui/react"
import { breakpoints } from "./foundations/breakpoints"
import { colors } from "./foundations/colors"
import { effects } from "./foundations/effects"
import { radii } from "./foundations/radii"
import { transitions } from "./foundations/transitions"
import { typography } from "./foundations/typography"

// Create and export the theme system
export const system = createSystem(defaultConfig, {
  theme: {
    breakpoints,
    keyframes: {
      fadeIn: {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      slideUp: {
        from: { transform: "translateY(20px)", opacity: 0 },
        to: { transform: "translateY(0)", opacity: 1 },
      },
    },
    semanticTokens: {
      colors: {
        // Dark-first: base = dark value, _light = light override
        bg: {
          DEFAULT: { value: { base: "#0e1228", _light: "#f7f8fc" } },
          subtle: {
            value: { base: "rgba(255, 255, 255, 0.06)", _light: "#f0f2f8" },
          },
          muted: {
            value: { base: "rgba(255, 255, 255, 0.04)", _light: "#edf0f7" },
          },
          emphasized: {
            value: { base: "rgba(255, 255, 255, 0.08)", _light: "#e2e6f0" },
          },
          panel: { value: { base: "#0e1228", _light: "white" } },
          inverted: { value: { base: "white", _light: "#0f152c" } },
        },
        fg: {
          DEFAULT: { value: { base: "#d8e0f4", _light: "#1a202c" } },
          muted: { value: { base: "#A0AEC0", _light: "#4A5568" } },
          subtle: { value: { base: "#718096", _light: "#718096" } },
          inverted: { value: { base: "#1a202c", _light: "#d8e0f4" } },
        },
        border: {
          DEFAULT: {
            value: { base: "rgba(255, 255, 255, 0.08)", _light: "#E2E8F0" },
          },
          muted: {
            value: { base: "rgba(255, 255, 255, 0.06)", _light: "#EDF2F7" },
          },
          subtle: {
            value: { base: "rgba(255, 255, 255, 0.04)", _light: "#F7FAFC" },
          },
        },
        "colorPalette.fg": { value: colors.brand[200] },
        "colorPalette.solid": { value: colors.brand[200] },
        "colorPalette.subtle": { value: "rgba(52, 161, 221, 0.1)" },
        "colorPalette.focusRing": { value: colors.brand[200] },
      },
    },
  },
  globalCss: {
    "html, body": {
      height: "100%",
      bg: "#0e1228",
      color: "#d8e0f4",
      fontFamily: typography.fonts.body,
      fontSize: { base: "14px", md: "15px", lg: "16px" },
      fontWeight: "400",
      lineHeight: "1.5",
      overflowX: "hidden",
      width: "100%",
      maxWidth: "100vw",
    },
    "#root": {
      overflowX: "hidden",
      width: "100%",
      maxWidth: "100vw",
    },
    // Responsive media. SVG is intentionally excluded — react-icons
    // sets width/height attributes per icon (e.g. `<FiLogOut size={18} />`),
    // and `height: auto` here would clobber those and collapse the icon.
    "img, video, canvas": {
      maxWidth: "100%",
      height: "auto",
    },
    // Light mode overrides
    ".light, .light body": {
      bg: "#f7f8fc !important",
      color: "#1a202c !important",
      background:
        "radial-gradient(circle at 10% 10%, rgba(24, 109, 56, 0.06) 0%, transparent 36%), radial-gradient(circle at 84% 15%, rgba(52, 161, 221, 0.06) 0%, transparent 34%), #f7f8fc !important",
    },
    // Light mode grid overlay
    ".light body::before": {
      backgroundImage:
        "linear-gradient(rgba(52, 161, 221, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(52, 161, 221, 0.04) 1px, transparent 1px) !important",
      maskImage:
        "radial-gradient(circle at center, black 28%, transparent 84%) !important",
    },
    // Light mode glow orbs
    ".light body::after": {
      background:
        "radial-gradient(circle at 0% 0%, rgba(24, 109, 56, 0.08) 0%, transparent 50%), radial-gradient(circle at 100% 30%, rgba(52, 161, 221, 0.08) 0%, transparent 50%) !important",
    },
    ".light *::placeholder": {
      color: "#A0AEC0 !important",
    },
    ".light *, .light *::before, .light *::after": {
      borderColor: "#E2E8F0 !important",
    },
    ".light label": {
      color: "#1a202c !important",
    },
    ".light th": {
      color: "#4A5568 !important",
      borderColor: "#E2E8F0 !important",
    },
    ".light td": {
      borderColor: "#EDF2F7 !important",
    },
    ".light a": {
      color: `${colors.brand[500]} !important`,
    },
    ".light a:hover": {
      color: `${colors.brand[600]} !important`,
    },
    body: {
      background:
        "radial-gradient(circle at 10% 10%, rgba(24, 109, 56, 0.34) 0%, transparent 36%), radial-gradient(circle at 84% 15%, rgba(52, 161, 221, 0.34) 0%, transparent 34%), #0e1228",
      backgroundAttachment: "fixed",
      minHeight: "100vh",
      position: "relative",
      // Grid overlay via ::before pseudo-element
      "&::before": {
        content: '""',
        position: "fixed",
        inset: "0",
        backgroundImage:
          "linear-gradient(rgba(52, 161, 221, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(52, 161, 221, 0.08) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        maskImage:
          "radial-gradient(circle at center, black 28%, transparent 84%)",
        WebkitMaskImage:
          "radial-gradient(circle at center, black 28%, transparent 84%)",
        pointerEvents: "none",
        zIndex: "0",
      },
      // Glow orbs via ::after pseudo-element
      "&::after": {
        content: '""',
        position: "fixed",
        inset: "0",
        background:
          "radial-gradient(circle at 0% 0%, rgba(24, 109, 56, 0.25) 0%, transparent 35%), radial-gradient(circle at 100% 30%, rgba(52, 161, 221, 0.25) 0%, transparent 35%)",
        pointerEvents: "none",
        zIndex: "0",
      },
      "&::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        background: colors.glass.whiteAlpha[300],
        borderRadius: "4px",
        "&:hover": {
          background: colors.glass.whiteAlpha[400],
        },
      },
    },
    "*::placeholder": {
      color: "gray.400",
    },
    "*, *::before, *::after": {
      borderColor: colors.glass.whiteAlpha[200],
      boxSizing: "border-box",
    },
    // Form label styles
    label: {
      color: "white",
      fontSize: "sm",
      fontWeight: "500",
      mb: "8px",
      display: "block",
    },
    // Table styles
    table: {
      borderCollapse: "separate",
      borderSpacing: "0",
    },
    th: {
      color: "gray.400",
      fontSize: "xs",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "wider",
      borderBottom: "1px solid",
      borderColor: colors.glass.whiteAlpha[100],
      pb: "15px",
    },
    td: {
      fontSize: "sm",
      fontWeight: "500",
      borderBottom: "1px solid",
      borderColor: colors.glass.whiteAlpha[100],
      py: "20px",
    },
    "tbody tr:last-child td": {
      borderBottom: "none",
    },
    // Chakra Tabs - dark mode overrides
    ".chakra-tabs__trigger, [data-scope='tabs'][data-part='trigger']": {
      color: "#A0AEC0 !important",
      fontWeight: "600",
      "&[data-selected]": {
        color: "white !important",
        "--indicator-color": `${colors.brand[200]} !important`,
      },
      "&:hover": {
        color: "white !important",
      },
    },
    ".light .chakra-tabs__trigger, .light [data-scope='tabs'][data-part='trigger']":
      {
        color: "#718096 !important",
        "&[data-selected]": {
          color: "#1a202c !important",
        },
        "&:hover": {
          color: "#1a202c !important",
        },
      },
    // Chakra Tabs enclosed variant - dark list background
    ".chakra-tabs__list, [data-scope='tabs'][data-part='list']": {
      borderColor: `${colors.glass.whiteAlpha[200]} !important`,
    },
    ".light .chakra-tabs__list, .light [data-scope='tabs'][data-part='list']": {
      borderColor: "#E2E8F0 !important",
    },
    // Chakra Table - force transparent background in dark mode
    "table tr, table thead, .chakra-table tr, .chakra-table thead, [data-scope='table'] tr, [data-scope='table'] thead":
      {
        bg: "transparent !important",
        background: "transparent !important",
      },
    "table th, .chakra-table th, [data-scope='table'] th": {
      color: "#A0AEC0 !important",
      bg: "transparent !important",
    },
    ".light table th, .light .chakra-table th, .light [data-scope='table'] th":
      {
        color: "#4A5568 !important",
      },
    ".light table tr, .light .chakra-table tr, .light [data-scope='table'] tr":
      {
        bg: "transparent !important",
        background: "transparent !important",
      },
    // Link styles
    a: {
      color: colors.brand[200],
      textDecoration: "none",
      transition: "color 0.2s",
      _hover: {
        color: colors.brand[300],
      },
    },
    // Dropdown / Select / Menu / Popover content - dark mode fix
    "[data-scope='select'][data-part='content'], [data-scope='menu'][data-part='content'], [data-scope='popover'][data-part='content'], [data-scope='combobox'][data-part='content']":
      {
        bg: "#0e1228 !important",
        color: "#d8e0f4 !important",
        borderColor: `${colors.glass.whiteAlpha[200]} !important`,
        backdropFilter: "blur(20px)",
        boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.4) !important",
      },
    "[data-scope='select'][data-part='item'], [data-scope='menu'][data-part='item'], [data-scope='combobox'][data-part='item']":
      {
        bg: "transparent !important",
        color: "#d8e0f4 !important",
        _hover: {
          bg: `${colors.glass.whiteAlpha[100]} !important`,
        },
        "&[data-highlighted]": {
          bg: `${colors.glass.whiteAlpha[100]} !important`,
          color: "#edf3ff !important",
        },
        "&[data-state='checked']": {
          bg: `${colors.glass.whiteAlpha[200]} !important`,
          color: `${colors.brand[200]} !important`,
        },
      },
    "[data-scope='select'][data-part='item-group-label'], [data-scope='menu'][data-part='item-group-label']":
      {
        color: "#A0AEC0 !important",
      },
    // Dropdown - light mode
    ".light [data-scope='select'][data-part='content'], .light [data-scope='menu'][data-part='content'], .light [data-scope='popover'][data-part='content'], .light [data-scope='combobox'][data-part='content']":
      {
        bg: "white !important",
        color: "#1a202c !important",
        borderColor: "#E2E8F0 !important",
        backdropFilter: "none",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
      },
    ".light [data-scope='select'][data-part='item'], .light [data-scope='menu'][data-part='item'], .light [data-scope='combobox'][data-part='item']":
      {
        bg: "transparent !important",
        color: "#1a202c !important",
        "&[data-highlighted]": {
          bg: "#EDF2F7 !important",
          color: "#1a202c !important",
        },
        "&[data-state='checked']": {
          bg: "#EDF2F7 !important",
          color: `${colors.brand[500]} !important`,
        },
      },
    // Native select/option dark mode fix
    "select, select option": {
      bg: "#0e1228 !important",
      color: "#d8e0f4 !important",
    },
    ".light select, .light select option": {
      bg: "white !important",
      color: "#1a202c !important",
    },
    // Glass morphism utilities
    ".glass": {
      bg: colors.glass.dark,
      backdropFilter: "blur(20px)",
      border: "1px solid",
      borderColor: colors.glass.whiteAlpha[200],
    },
    ".glass-gradient": {
      background: `linear-gradient(135deg, ${colors.glass.whiteAlpha[100]} 0%, ${colors.glass.whiteAlpha[50]} 100%)`,
      backdropFilter: "blur(20px)",
      border: "1px solid",
      borderColor: colors.glass.whiteAlpha[200],
    },
  },
})

export const theme = system

// Export theme parts for component usage
export { colors, effects, typography, breakpoints, radii, transitions }
